import { makeStyles } from 'tss-react/mui';

/**
 * Shared visual treatment for the 32x32 icon buttons that sit under a
 * message — copy, thumbs-up, thumbs-down and the feedback overflow.
 *
 * Sizing matches the GA-Sidekick Figma source: a 32x32 hit area per
 * button (16px icon + 8px padding on each side) and an 8px
 * border-radius wrapping the active and hover fills.
 *
 * Hover and focus-visible reuse `theme.new.color.brand[100]` — the
 * same fill as the active state — so the affordance previews what an
 * engaged button looks like. The active state additionally recolors
 * the glyph stroke to `theme.new.color.brand[600]` so engaged buttons
 * render in brand blue, matching the Figma source (the active
 * variants use a literally different SVG with `stroke=#0072CE` — see
 * frames `16166:21109` and `16166:21117`). Focus-visible adds an
 * explicit 2px brand outline (offset 2px) on top of the fill so
 * keyboard focus is unambiguous even when the button is already
 * active.
 *
 * Row layout (gap, alignment) belongs to the consuming row, not here,
 * so this module stays usable by a single standalone button.
 */
const useMessageActionButtonStyles = makeStyles()((theme) => ({
  button: {
    flexShrink: 0,
    width: 32,
    height: 32,
    padding: theme.new.spacing.small,
    borderRadius: theme.new.shape.borderRadius.small,
    color: theme.new.color.textPrimary,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: theme.new.color.brand[100],
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.new.color.brand[600]}`,
      outlineOffset: '2px',
    },
  },
  buttonActive: {
    backgroundColor: theme.new.color.brand[100],
    color: theme.new.color.brand[600],
    '&:hover': {
      backgroundColor: theme.new.color.brand[100],
      color: theme.new.color.brand[600],
    },
  },
}));

export default useMessageActionButtonStyles;
